import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Textarea,
  Switch,
  Grid,
  Select,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { appVersionSchema, type AppVersionFormData } from '@/lib/validations/appVersion'
import { AppVersionService } from '@/lib/services/appVersionService'

interface AppVersionFormProps {
  appVersionId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type ApplicationOption = { id: number; name: string; code: string }

export function AppVersionForm({ appVersionId, onSuccess, onCancel }: AppVersionFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [applications, setApplications] = useState<ApplicationOption[]>([])
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!appVersionId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AppVersionFormData>({
    resolver: zodResolver(appVersionSchema),
    defaultValues: {
      application_id: 0,
      version: '',
      build: '',
      min_supported_version: '',
      is_mandatory: false,
      rollout_percent: 100,
      release_notes: '',
      download_url: '',
      checksum: '',
      published_at: '',
    },
  })

  const isMandatory = watch('is_mandatory')

  useEffect(() => {
    apiClient.get('/system-settings/applications?per_page=100').then((res) => {
      const rows = res.data?.data ?? res.data ?? []
      setApplications(Array.isArray(rows) ? rows : [])
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (isEditMode && appVersionId) {
      const load = async () => {
        setIsLoading(true)
        try {
          const res = await AppVersionService.get(appVersionId)
          const d = (res as any)?.data ?? res ?? {}
          reset({
            application_id: d.application_id ?? 0,
            version: d.version ?? '',
            build: d.build ?? '',
            min_supported_version: d.min_supported_version ?? '',
            is_mandatory: Boolean(d.is_mandatory),
            rollout_percent: d.rollout_percent ?? 100,
            release_notes: d.release_notes ?? '',
            download_url: d.download_url ?? '',
            checksum: d.checksum ?? '',
            published_at: d.published_at ? d.published_at.slice(0, 16).replace(' ', 'T') : '',
          })
        } catch (e) {
          showError(e)
        } finally {
          setIsLoading(false)
        }
      }
      load()
    }
  }, [appVersionId, isEditMode, reset, showError])

  const onSubmit = async (data: AppVersionFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        published_at: data.published_at || null,
      }
      if (isEditMode && appVersionId) {
        await AppVersionService.update(appVersionId, payload)
        showSuccess('Success', 'App version updated')
      } else {
        await AppVersionService.create(payload)
        showSuccess('Success', 'App version created')
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <Text fontSize="sm" color="gray.500">Loading…</Text>
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
        <FormControl isInvalid={!!errors.application_id} isRequired>
          <FormLabel>Application</FormLabel>
          <Select {...register('application_id')} placeholder="Select application">
            {applications.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name} ({app.code})
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.application_id?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.version} isRequired>
          <FormLabel>Version</FormLabel>
          <Input {...register('version')} placeholder="e.g. 1.2.0" />
          <FormErrorMessage>{errors.version?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.build}>
          <FormLabel>Build</FormLabel>
          <Input {...register('build')} placeholder="e.g. 120" />
        </FormControl>

        <FormControl isInvalid={!!errors.min_supported_version}>
          <FormLabel>Min Supported Version</FormLabel>
          <Input {...register('min_supported_version')} />
        </FormControl>

        <FormControl isInvalid={!!errors.rollout_percent}>
          <FormLabel>Rollout %</FormLabel>
          <Input type="number" min={0} max={100} {...register('rollout_percent')} />
        </FormControl>

        <FormControl isInvalid={!!errors.download_url} isRequired>
          <FormLabel>Download URL</FormLabel>
          <Input {...register('download_url')} />
          <FormErrorMessage>{errors.download_url?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.checksum}>
          <FormLabel>Checksum</FormLabel>
          <Input {...register('checksum')} />
        </FormControl>

        <FormControl>
          <FormLabel>Mandatory Update</FormLabel>
          <Switch isChecked={isMandatory} onChange={(e) => setValue('is_mandatory', e.target.checked)} />
        </FormControl>

        <FormControl isInvalid={!!errors.published_at}>
          <FormLabel>Published At</FormLabel>
          <Input type="datetime-local" {...register('published_at')} />
        </FormControl>

        <FormControl gridColumn={{ md: 'span 2' }} isInvalid={!!errors.release_notes}>
          <FormLabel>Release Notes</FormLabel>
          <Textarea rows={4} {...register('release_notes')} />
        </FormControl>
      </Grid>

      <HStack mt={6} spacing={3}>
        <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
          {isEditMode ? 'Update' : 'Create'}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </HStack>
    </Box>
  )
}
